import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  FileText,
  RefreshCw,
  Search,
  Wallet,
} from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type {
  EmpresaCapturaAdmin,
  PanoramaCapturaAdmin,
} from '../../api/tipos';
import {
  Abas,
  Aviso,
  BarraFerramentas,
  Botao,
  Campo,
  Cartao,
  Celula,
  Crescer,
  Definicoes,
  Esqueleto,
  Etiqueta,
  GraficoBarras,
  Indicador,
  Indicadores,
  Paginacao,
  Tabela,
  Td,
  Th,
  TituloSecao,
  Vazio,
  type PontoGrafico,
  type TomEtiqueta,
} from '../../componentes/ui';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import { numero, plural, reais, tempoRelativo } from '../../util/formato';
import { cnpjLegivel, diasParaVencer } from '../painel/captura/status';
import css from './CapturaAdmin.module.css';

/**
 * Operação da captura automática (DF-e), vista pelo administrador.
 *
 * A pergunta da tela é dupla: "a captura está saudável?" (laço vivo, ninguém
 * atrasado, certificados em dia) e "está dando dinheiro?" (planos debitados
 * no mês e a receita recorrente). Só leitura — nenhuma ação sobre a empresa de um cliente
 * mora aqui, como no resto do painel administrativo.
 */

/** O laço roda de minuto em minuto; a tela acompanha no mesmo ritmo. */
const INTERVALO_MS = 60_000;
/** Sem volta do laço há mais que isto = laço parado, mesmo "ligado". */
const TICK_SUSPEITO_MS = 3 * 60_000;
const DIAS_GRAFICO = 30;
const POR_PAGINA = 25;

type Filtro = 'todas' | 'problema' | 'pausadas';

/** Certificado vence em até 30 dias (ou já venceu)? */
function certificadoCritico(e: EmpresaCapturaAdmin): boolean {
  return (
    !!e.certificadoValidoAte && diasParaVencer(e.certificadoValidoAte) <= 30
  );
}

function temProblema(e: EmpresaCapturaAdmin): boolean {
  if (!e.ativa)
    return e.motivoPausa === 'SALDO' || e.motivoPausa === 'CERTIFICADO';
  return (
    e.atrasada ||
    !!e.ultimoErro ||
    !!e.inadimplenteDesde ||
    !e.certificadoValidoAte ||
    certificadoCritico(e)
  );
}

function situacao(e: EmpresaCapturaAdmin): { tom: TomEtiqueta; texto: string } {
  if (!e.ativa) {
    if (e.motivoPausa === 'SALDO')
      return { tom: 'erro', texto: 'Pausada · sem saldo' };
    if (e.motivoPausa === 'CERTIFICADO')
      return { tom: 'erro', texto: 'Pausada · certificado' };
    return { tom: 'neutro', texto: 'Pausada pelo cliente' };
  }
  if (e.atrasada) return { tom: 'erro', texto: 'Atrasada' };
  if (e.inadimplenteDesde) return { tom: 'alerta', texto: 'Plano em atraso' };
  if (e.ultimoErro) return { tom: 'alerta', texto: 'Ativa · com aviso' };
  return { tom: 'ok', texto: 'Capturando' };
}

/** Últimos 30 dias, com zero nos dias sem nota — senão o eixo mente. */
function serieCompleta(
  serie: PanoramaCapturaAdmin['serieDiaria'],
): PontoGrafico[] {
  const porDia = new Map(serie.map((s) => [s.dia, s]));
  const hoje = new Date();
  const base = Date.UTC(
    hoje.getUTCFullYear(),
    hoje.getUTCMonth(),
    hoje.getUTCDate(),
  );
  return Array.from({ length: DIAS_GRAFICO }, (_, i) => {
    const dia = new Date(base - (DIAS_GRAFICO - 1 - i) * 86_400_000)
      .toISOString()
      .slice(0, 10);
    const s = porDia.get(dia);
    return {
      rotulo: `${dia.slice(8, 10)}/${dia.slice(5, 7)}`,
      valor: s?.total ?? 0,
      secundario: s?.completas ?? 0,
    };
  });
}

function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

export function CapturaAdmin() {
  const [dados, setDados] = useState<PanoramaCapturaAdmin | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [atualizando, setAtualizando] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    setAtualizando(true);
    try {
      setDados(await api<PanoramaCapturaAdmin>('/admin/captura'));
      setErro(null);
    } catch (err) {
      // Mantém o último panorama na tela: um tropeço da rede não deve apagar
      // o que o administrador está olhando.
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Falha ao carregar o panorama da captura.',
      );
    } finally {
      setAtualizando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
    const t = setInterval(() => void carregar(), INTERVALO_MS);
    return () => clearInterval(t);
  }, [carregar]);

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Captura automática"
        descricao="Se o laço de consulta à SEFAZ está saudável, quanto a captura rende e quais empresas pedem atenção."
        acoes={
          <Botao
            aparencia="secundario"
            icone={<RefreshCw />}
            carregando={atualizando}
            onClick={() => void carregar()}
          >
            Atualizar
          </Botao>
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      {dados === null ? (
        erro ? null : (
          <Carregando />
        )
      ) : (
        <>
          <Operacao dados={dados} />
          <Numeros dados={dados} />
          <Cartao
            titulo="Notas capturadas por dia"
            descricao="Últimos 30 dias, com as que já têm o XML completo."
          >
            <GraficoBarras
              pontos={serieCompleta(dados.serieDiaria)}
              legenda={['Notas recebidas', 'Com XML completo']}
            />
          </Cartao>
          <Detalhes dados={dados} />
          <ListaEmpresas
            lista={dados.lista}
            filtro={filtro}
            aoFiltrar={(f) => {
              setFiltro(f);
              setPagina(1);
            }}
            busca={busca}
            aoBuscar={(b) => {
              setBusca(b);
              setPagina(1);
            }}
            pagina={pagina}
            aoPaginar={setPagina}
          />
        </>
      )}
    </Pagina>
  );
}

function Carregando() {
  return (
    <>
      <Esqueleto altura={56} />
      <div className={css.esqueletoIndicadores}>
        {[0, 1, 2, 3].map((i) => (
          <Esqueleto key={i} altura={112} />
        ))}
      </div>
      <Esqueleto altura={260} />
    </>
  );
}

/* ── Faixa de operação ─────────────────────────────────────────────────── */

function Operacao({ dados }: { dados: PanoramaCapturaAdmin }) {
  const { operacao: op, config } = dados;
  const ultimo = op.ultimoTick
    ? Date.now() - new Date(op.ultimoTick).getTime()
    : null;
  const lacoVivo =
    op.lacoLigado && ultimo !== null && ultimo < TICK_SUSPEITO_MS;

  return (
    <>
      <Cartao compacto>
        <div className={css.faixa}>
          <div className={css.selos}>
            {op.ambiente === 'producao' ? (
              <Etiqueta tom="ok">Produção</Etiqueta>
            ) : (
              <Etiqueta tom="info">Homologação</Etiqueta>
            )}
            {op.somenteAdmin && (
              <Etiqueta tom="alerta">Liberado só para admin</Etiqueta>
            )}
            {!op.habilitado ? (
              <Etiqueta tom="erro">Desabilitada</Etiqueta>
            ) : lacoVivo ? (
              <Etiqueta tom="ok" pulsando>
                Laço ativo
              </Etiqueta>
            ) : op.lacoLigado ? (
              <Etiqueta tom="alerta">Laço sem volta recente</Etiqueta>
            ) : (
              <Etiqueta tom="erro">Laço desligado</Etiqueta>
            )}
            <span className={css.meta}>
              Último ciclo:{' '}
              {op.ultimoTick
                ? tempoRelativo(op.ultimoTick)
                : 'nenhum neste processo'}
            </span>
          </div>
          <span className={css.meta}>
            Plano{' '}
            {config.faixas
              .map(
                (f) =>
                  `${f.ate === 1 ? '1 CNPJ' : `até ${f.ate}`} ${reais(f.centavos)}`,
              )
              .join(' · ')}{' '}
            · +{reais(config.adicionalCentavos)}/CNPJ acima · XMLs ilimitados ·
            carência {plural(config.carenciaDias, 'dia')}
          </span>
        </div>
      </Cartao>

      {!op.habilitado && (
        <Aviso titulo="Captura desabilitada">
          Sem a chave mestra dos certificados (CERT_MASTER_KEY) o servidor não
          aceita certificado nem consulta a SEFAZ.
        </Aviso>
      )}
      {op.habilitado && !op.lacoLigado && (
        <Aviso titulo="Laço de captura desligado">
          Nenhuma empresa está sendo consultada por este processo
          (DFE_CAPTURA=off ou a rotina não subiu). As notas novas só chegam
          quando o laço voltar.
        </Aviso>
      )}
      {op.empresasAtrasadas > 0 && (
        <Aviso
          tom="alerta"
          titulo={plural(
            op.empresasAtrasadas,
            'empresa atrasada',
            'empresas atrasadas',
          )}
        >
          Consulta vencida há mais de 20 minutos — sinal de laço travado ou
          lento. Veja a aba “Com problema” abaixo.
        </Aviso>
      )}
    </>
  );
}

/* ── Indicadores ───────────────────────────────────────────────────────── */

function Numeros({ dados }: { dados: PanoramaCapturaAdmin }) {
  const { empresas, notas, receita, certificados, planos } = dados;
  const certificadosCriticos =
    certificados.vencendo30Dias + certificados.vencidos;
  const pendencias = certificadosCriticos + planos.inadimplentes;

  return (
    <Indicadores>
      <Indicador
        rotulo="Empresas ativas"
        icone={<Building2 />}
        valor={numero(empresas.ativas)}
        detalhe={`de ${numero(empresas.total)} · ${plural(empresas.clientes, 'cliente')}`}
      />
      <Indicador
        rotulo="Notas capturadas"
        icone={<FileText />}
        valor={numero(notas.total)}
        detalhe={`${numero(notas.ultimas24h)} nas últimas 24 h · ${numero(notas.completas)} com XML`}
      />
      <Indicador
        rotulo="Receita do mês"
        icone={<Wallet />}
        valor={reais(receita.mes)}
        tom={receita.mes > 0 ? 'ok' : undefined}
        detalhe={`${plural(planos.vigentes, 'plano em dia', 'planos em dia')} · ${reais(planos.recorrenteCentavos)}/mês recorrente`}
      />
      <Indicador
        rotulo="Pendências"
        icone={<AlertTriangle />}
        valor={numero(pendencias)}
        tom={pendencias > 0 ? 'alerta' : 'ok'}
        detalhe={
          <span className={css.pendencias}>
            <span>
              {plural(
                certificadosCriticos,
                'certificado vencendo',
                'certificados vencendo',
              )}
            </span>
            <span>
              {plural(
                planos.inadimplentes,
                'conta inadimplente',
                'contas inadimplentes',
              )}
            </span>
          </span>
        }
      />
    </Indicadores>
  );
}

function Detalhes({ dados }: { dados: PanoramaCapturaAdmin }) {
  const { notas, empresas, certificados, receita } = dados;
  return (
    <Cartao titulo="Detalhamento">
      <div className={css.detalhes}>
        <div>
          <h4 className={css.subtitulo}>Notas sem XML completo</h4>
          <Definicoes
            itens={[
              ['Aguardando XML', numero(notas.aguardandoXml)],
              ['Fora do prazo', numero(notas.foraDoPrazo)],
              ['Erro na Ciência', numero(notas.comErroManifestacao)],
            ]}
          />
        </div>
        <div>
          <h4 className={css.subtitulo}>Empresas pausadas</h4>
          <Definicoes
            itens={[
              ['Pelo cliente', numero(empresas.pausadasCliente)],
              ['Sem saldo', numero(empresas.pausadasSaldo)],
              ['Certificado', numero(empresas.pausadasCertificado)],
              ['Ativas com erro', numero(empresas.comErro)],
            ]}
          />
        </div>
        <div>
          <h4 className={css.subtitulo}>Certificados</h4>
          <Definicoes
            itens={[
              ['Cadastrados', numero(certificados.total)],
              ['Vencendo em 30 dias', numero(certificados.vencendo30Dias)],
              ['Vencidos', numero(certificados.vencidos)],
            ]}
          />
        </div>
        <div>
          <h4 className={css.subtitulo}>Receita acumulada</h4>
          <Definicoes
            itens={[
              ['Total debitado', reais(receita.total)],
              ['Recorrente/mês', reais(dados.planos.recorrenteCentavos)],
            ]}
          />
        </div>
      </div>
    </Cartao>
  );
}

/* ── Empresas ──────────────────────────────────────────────────────────── */

function ListaEmpresas({
  lista,
  filtro,
  aoFiltrar,
  busca,
  aoBuscar,
  pagina,
  aoPaginar,
}: {
  lista: EmpresaCapturaAdmin[];
  filtro: Filtro;
  aoFiltrar: (f: Filtro) => void;
  busca: string;
  aoBuscar: (b: string) => void;
  pagina: number;
  aoPaginar: (p: number) => void;
}) {
  const contagem = useMemo(
    () => ({
      todas: lista.length,
      problema: lista.filter(temProblema).length,
      pausadas: lista.filter((e) => !e.ativa).length,
    }),
    [lista],
  );

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const digitos = termo.replace(/\D/g, '');
    return lista.filter((e) => {
      if (filtro === 'problema' && !temProblema(e)) return false;
      if (filtro === 'pausadas' && e.ativa) return false;
      if (!termo) return true;
      return (
        e.nome.toLowerCase().includes(termo) ||
        e.cliente.email.toLowerCase().includes(termo) ||
        e.cliente.nome.toLowerCase().includes(termo) ||
        (digitos.length > 0 && e.cnpj.includes(digitos))
      );
    });
  }, [lista, filtro, busca]);

  const visiveis = filtradas.slice(
    (pagina - 1) * POR_PAGINA,
    pagina * POR_PAGINA,
  );

  return (
    <section className={css.secao}>
      <TituloSecao descricao="Todas as empresas monitoradas, das mais recentes para as mais antigas.">
        Empresas
      </TituloSecao>
      <Cartao padding={0}>
        <BarraFerramentas>
          <Abas
            rotulo="Filtrar empresas"
            atual={filtro}
            aoMudar={aoFiltrar}
            opcoes={[
              { valor: 'todas', rotulo: 'Todas', contagem: contagem.todas },
              {
                valor: 'problema',
                rotulo: 'Com problema',
                contagem: contagem.problema,
              },
              {
                valor: 'pausadas',
                rotulo: 'Pausadas',
                contagem: contagem.pausadas,
              },
            ]}
          />
          <Crescer>
            <Campo
              rotulo="Buscar empresa"
              rotuloOculto
              type="search"
              icone={<Search />}
              placeholder="Buscar por empresa, CNPJ ou cliente"
              value={busca}
              onChange={(e) => aoBuscar(e.target.value)}
            />
          </Crescer>
        </BarraFerramentas>

        {lista.length === 0 ? (
          <Vazio
            icone={<Building2 />}
            titulo="Nenhuma empresa monitorada"
            descricao="Quando um cliente cadastrar o certificado e ligar a captura de um CNPJ, a empresa aparece aqui."
          />
        ) : filtradas.length === 0 ? (
          <Vazio
            icone={<Search />}
            titulo="Nada neste filtro"
            descricao={
              filtro === 'problema' && !busca
                ? 'Nenhuma empresa com problema agora.'
                : 'Nenhuma empresa corresponde à busca e ao filtro escolhidos.'
            }
          />
        ) : (
          <>
            <Tabela rotulo="Empresas da captura">
              <thead>
                <tr>
                  <Th>Cliente</Th>
                  <Th>Empresa</Th>
                  <Th>Situação</Th>
                  <Th alinhar="direita">Notas</Th>
                  <Th>Última consulta</Th>
                  <Th>Certificado</Th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((e) => (
                  <LinhaEmpresa key={e.id} e={e} />
                ))}
              </tbody>
            </Tabela>
            <Paginacao
              total={filtradas.length}
              pagina={pagina}
              porPagina={POR_PAGINA}
              aoMudar={aoPaginar}
              rotulo={filtradas.length === 1 ? 'empresa' : 'empresas'}
            />
          </>
        )}
      </Cartao>
    </section>
  );
}

function LinhaEmpresa({ e }: { e: EmpresaCapturaAdmin }) {
  const s = situacao(e);
  const dias = e.certificadoValidoAte
    ? diasParaVencer(e.certificadoValidoAte)
    : null;

  return (
    <tr>
      <Td>
        <Celula
          principal={e.cliente.nome || e.cliente.email}
          secundaria={e.cliente.email}
        />
      </Td>
      <Td>
        <Celula
          principal={e.nome}
          secundaria={<span className="num">{cnpjLegivel(e.cnpj)}</span>}
        />
      </Td>
      <Td>
        <Etiqueta tom={s.tom}>{s.texto}</Etiqueta>
        {e.ultimoErro && (
          <span className={css.erro} title={e.ultimoErro}>
            {e.ultimoErro}
          </span>
        )}
      </Td>
      <Td alinhar="direita">
        <Celula
          principal={<span className="num">{numero(e.notas)}</span>}
          secundaria={e.planoVigente ? 'Plano em dia' : 'Sem plano vigente'}
        />
      </Td>
      <Td>
        <Celula
          principal={
            e.ultimaConsultaEm ? tempoRelativo(e.ultimaConsultaEm) : 'Nunca'
          }
          secundaria={
            e.ultimoCStat !== null
              ? `cStat ${e.ultimoCStat} · próxima ${tempoFuturo(e.proximaConsultaEm)}`
              : `Próxima ${tempoFuturo(e.proximaConsultaEm)}`
          }
        />
      </Td>
      <Td>
        {dias === null ? (
          <Etiqueta tom="erro">Sem certificado</Etiqueta>
        ) : (
          <Celula
            principal={dataCurta(e.certificadoValidoAte!)}
            secundaria={
              <span
                className={
                  dias <= 0 ? css.critico : dias <= 30 ? css.atencao : undefined
                }
              >
                {dias <= 0 ? 'Vencido' : `Vence em ${plural(dias, 'dia')}`}
              </span>
            }
          />
        )}
      </Td>
    </tr>
  );
}

/** "em 12 min", "agora", "atrasada há 3 h" — a próxima consulta agendada. */
function tempoFuturo(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return ms > -60_000 ? 'agora' : `vencida ${tempoRelativo(iso)}`;
  const min = Math.round(ms / 60_000);
  if (min < 60) return `em ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `em ${h} h`;
  return `em ${Math.round(h / 24)} dias`;
}
